import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as FinancialIcon,
  Speed as OperationalIcon,
  TrendingUp as MarketIcon,
  Warning as RiskIcon,
  Business as BusinessModelIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Compare as CompareIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

import KPICard, { KPIData } from '../KPICard/KPICard';

interface KPISectionProps {
  kpis: KPIData[];
  title?: string;
  showCategoryFilter?: boolean;
  defaultCategory?: string;
  compact?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kpi-tabpanel-${index}`}
      aria-labelledby={`kpi-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const KPISection: React.FC<KPISectionProps> = ({
  kpis,
  title = 'Key Performance Indicators',
  showCategoryFilter = true,
  defaultCategory = 'all',
  compact = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showBenchmarks, setShowBenchmarks] = useState(true);
  const [showDescriptions, setShowDescriptions] = useState(false);

  const categories = [
    { id: 'all', label: 'All KPIs', icon: null },
    { id: 'financial', label: 'Financial', icon: FinancialIcon },
    { id: 'business_model', label: 'Business Model', icon: BusinessModelIcon },
    { id: 'operational', label: 'Operational', icon: OperationalIcon },
    { id: 'market', label: 'Market', icon: MarketIcon },
  ];

  const filteredKPIs =
    selectedCategory === 'all'
      ? kpis.filter(kpi => kpi.category !== 'risk')
      : kpis.filter(kpi => kpi.category === selectedCategory);

  const getCategoryStats = (category: string) => {
    const categoryKPIs =
      category === 'all' ? kpis.filter(kpi => kpi.category !== 'risk') : kpis.filter(kpi => kpi.category === category);
    const withBenchmarks = categoryKPIs.filter(kpi => kpi.benchmark);
    const excellent = categoryKPIs.filter(kpi => {
      if (!kpi.benchmark || typeof kpi.value !== 'number') return false;
      const threshold = kpi.isGoodHigh ? kpi.benchmark.p75 : kpi.benchmark.p25;
      return kpi.isGoodHigh ? kpi.value >= threshold : kpi.value <= threshold;
    });

    return {
      total: categoryKPIs.length,
      withBenchmarks: withBenchmarks.length,
      excellent: excellent.length,
    };
  };

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedCategory(categories[newValue].id);
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'list') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const currentCategoryIndex = categories.findIndex(cat => cat.id === selectedCategory);
  const stats = getCategoryStats(selectedCategory);

  return (
    <Box>
      {/* Header */}
      {title && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary.main" gutterBottom>
              {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip label={`${stats.total} KPIs`} size="small" color="primary" variant="outlined" />
              <Chip
                label={`${stats.excellent}/${stats.total} Excellent`}
                size="small"
                color="success"
                variant="outlined"
              />
              {stats.withBenchmarks > 0 && (
                <Chip label={`${stats.withBenchmarks} with benchmarks`} size="small" color="info" variant="outlined" />
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Toggle benchmark comparison">
              <Button
                startIcon={<CompareIcon />}
                variant={showBenchmarks ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setShowBenchmarks(!showBenchmarks)}
              >
                Benchmarks
              </Button>
            </Tooltip>

            <Tooltip title="Toggle descriptions">
              <Button
                startIcon={<DescriptionIcon />}
                variant={showDescriptions ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setShowDescriptions(!showDescriptions)}
              >
                Descriptions
              </Button>
            </Tooltip>

            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
              <ToggleButton value="grid">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list">
                <ListViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {!title && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip label={`${stats.total} KPIs`} size="small" color="primary" variant="outlined" />
            <Chip
              label={`${stats.excellent}/${stats.total} Excellent`}
              size="small"
              color="success"
              variant="outlined"
            />
            {stats.withBenchmarks > 0 && (
              <Chip label={`${stats.withBenchmarks} with benchmarks`} size="small" color="info" variant="outlined" />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Toggle benchmark comparison">
              <Button
                startIcon={<CompareIcon />}
                variant={showBenchmarks ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setShowBenchmarks(!showBenchmarks)}
              >
                Benchmarks
              </Button>
            </Tooltip>

            <Tooltip title="Toggle descriptions">
              <Button
                startIcon={<DescriptionIcon />}
                variant={showDescriptions ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setShowDescriptions(!showDescriptions)}
              >
                Descriptions
              </Button>
            </Tooltip>

            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
              <ToggleButton value="grid">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list">
                <ListViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {/* Category Tabs */}
      {showCategoryFilter && (
        <Box mb={3}>
          <Tabs
            value={currentCategoryIndex}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 'medium',
              },
            }}
          >
            {categories.map((category, index) => {
              const Icon = category.icon;
              const categoryStats = getCategoryStats(category.id);

              return (
                <Tab
                  key={category.id}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {Icon && <Icon fontSize="small" />}
                      <span>{category.label}</span>
                      <Chip
                        label={categoryStats.total}
                        size="small"
                        color={category.id === selectedCategory ? 'primary' : 'default'}
                        sx={{ ml: 0.5, minWidth: 24, height: 20 }}
                      />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      {/* KPI Cards */}
      <TabPanel value={currentCategoryIndex} index={currentCategoryIndex}>
        {filteredKPIs.length > 0 ? (
          <Box>
            {/* Group KPIs by category when showing all */}
            {selectedCategory === 'all' ? (
              <>
                {categories
                  .filter(cat => cat.id !== 'all')
                  .map(category => {
                    const categoryKPIs = kpis.filter(kpi => kpi.category === category.id && kpi.category !== 'risk');

                    if (categoryKPIs.length === 0) return null;

                    const Icon = category.icon;

                    return (
                      <Box key={category.id} mb={4}>
                        {/* Category Header */}
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          {Icon && <Icon fontSize="small" color="primary" />}
                          <Typography variant="h6" color="primary">
                            {category.label}
                          </Typography>
                          <Chip label={`${categoryKPIs.length} KPIs`} size="small" color="primary" variant="outlined" />
                        </Box>

                        {/* Category KPIs */}
                        <Grid container spacing={2}>
                          {categoryKPIs.map(kpi => (
                            <Grid
                              item
                              xs={viewMode === 'list' ? 12 : 6}
                              sm={viewMode === 'list' ? 12 : 6}
                              md={viewMode === 'list' ? 12 : 6}
                              lg={viewMode === 'list' ? 12 : 4}
                              xl={viewMode === 'list' ? 12 : 3}
                              key={kpi.id}
                              sx={{
                                display: 'flex',
                                '& > *': {
                                  minHeight: {
                                    xs: showDescriptions ? 400 : showBenchmarks ? 220 : 160,
                                    sm: showDescriptions ? 500 : showBenchmarks ? 280 : 180,
                                  },
                                  width: '100%',
                                },
                              }}
                            >
                              <KPICard
                                kpi={{
                                  ...kpi,
                                  benchmark: showBenchmarks ? kpi.benchmark : undefined,
                                }}
                                compact={compact || viewMode === 'list'}
                                showDescription={showDescriptions}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
              </>
            ) : (
              /* Single category view */
              <Grid container spacing={2}>
                {filteredKPIs.map(kpi => (
                  <Grid
                    item
                    xs={viewMode === 'list' ? 12 : 6}
                    sm={viewMode === 'list' ? 12 : 6}
                    md={viewMode === 'list' ? 12 : 6}
                    lg={viewMode === 'list' ? 12 : 4}
                    xl={viewMode === 'list' ? 12 : 3}
                    key={kpi.id}
                    sx={{
                      display: 'flex',
                      '& > *': {
                        minHeight: {
                          xs: showDescriptions ? 400 : showBenchmarks ? 220 : 160,
                          sm: showDescriptions ? 500 : showBenchmarks ? 280 : 180,
                        },
                        width: '100%',
                      },
                    }}
                  >
                    <KPICard
                      kpi={{
                        ...kpi,
                        benchmark: showBenchmarks ? kpi.benchmark : undefined,
                      }}
                      compact={compact || viewMode === 'list'}
                      showDescription={showDescriptions}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No KPIs available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCategory === 'all'
                ? 'No KPI data found for this company.'
                : `No ${categories.find(c => c.id === selectedCategory)?.label.toLowerCase()} KPIs available.`}
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Summary Footer */}
      {filteredKPIs.length > 0 && (
        <Box mt={4} p={2} bgcolor="action.hover" borderRadius={2}>
          <Typography variant="body2" color="text.secondary" align="center">
            Showing {filteredKPIs.length} of {kpis.length} KPIs
            {showBenchmarks && ` • Benchmarks are based on industry median and percentile data`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KPISection;
